import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send, Check, MessageCircle, Loader2 } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { useLang } from "../i18n.jsx";
import { api } from "../api/client.js";

const COPY = {
  id: {
    eyebrow: "Konsultasi Gratis",
    titleA: "Ceritakan kebutuhan Anda,",
    titleB: "kami balas maksimal 1\u00d724 jam.",
    sub: "Isi formulir singkat ini \u2014 tim studio menghubungi Anda lewat email atau WhatsApp untuk menjadwalkan konsultasi.",
    name: "Nama lengkap",
    email: "Email",
    phone: "No. WhatsApp (opsional)",
    company: "Instansi / Perusahaan (opsional)",
    message: "Ceritakan singkat kebutuhan Anda\u2026",
    plan: "Paket yang diminati",
    planNone: "Belum menentukan paket",
    submit: "Kirim & minta konsultasi",
    sending: "Mengirim\u2026",
    successTitle: "Terima kasih!",
    successBody: "Permintaan Anda sudah kami terima. Tim studio akan segera menghubungi Anda.",
    successAgain: "Kirim permintaan lain",
    error: "Gagal mengirim. Periksa koneksi Anda, atau hubungi kami lewat WhatsApp.",
    or: "atau langsung",
    wa: "Chat WhatsApp",
  },
  en: {
    eyebrow: "Free Consultation",
    titleA: "Tell us what you need,",
    titleB: "we reply within 24 hours.",
    sub: "Fill in this short form \u2014 our studio team will reach out by email or WhatsApp to schedule your consultation.",
    name: "Full name",
    email: "Email",
    phone: "WhatsApp number (optional)",
    company: "Company (optional)",
    message: "Briefly describe what you need\u2026",
    plan: "Interested package",
    planNone: "Not decided yet",
    submit: "Send & request consultation",
    sending: "Sending\u2026",
    successTitle: "Thank you!",
    successBody: "We have received your request. Our studio team will contact you shortly.",
    successAgain: "Send another request",
    error: "Could not send. Check your connection, or reach us on WhatsApp.",
    or: "or go straight to",
    wa: "WhatsApp chat",
  },
};

const EMPTY = { name: "", email: "", phone: "", company: "", message: "", planName: "" };

export default function LeadForm() {
  const { lang, waLink, content } = useLang();
  const c = COPY[lang] || COPY.id;
  const planNames = ((content && content.pricing && content.pricing.plans) || []).map((p) => p.name).filter(Boolean);
  const [form, setForm] = useState(EMPTY);
  const [state, setState] = useState("idle");

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  useEffect(() => {
    const onPick = (e) => setForm((f) => ({ ...f, planName: e.detail || "" }));
    window.addEventListener("ap:select-plan", onPick);
    return () => window.removeEventListener("ap:select-plan", onPick);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await api.createLead({ ...form, lang, source: form.planName ? "pricing" : "contact-form" });
      setState("done");
    } catch (err) {
      setState("error");
    }
  };

  const input =
    "w-full rounded-2xl border border-paper/15 bg-ink2/40 px-4 py-3 text-sm text-paper placeholder:text-paper/35 outline-none transition focus:border-acid";

  return (
    <section id="konsultasi" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -left-24 top-1/3 w-[420px] h-[420px] rounded-full bg-acid/[0.05] blur-3xl" />
      <motion.div
        initial={motionOk ? { opacity: 0, y: 26 } : false}
        whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
        viewport={{ once: false, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto grid max-w-6xl gap-10 lg:grid-cols-2"
      >
        <div>
          <p className="mb-4 text-[12px] uppercase tracking-[0.3em] text-acid">{c.eyebrow}</p>
          <h2 className="font-display font-extrabold tracking-tight text-[clamp(1.8rem,4.2vw,3.2rem)] leading-[1.08] text-paper">
            {c.titleA}
            <span className="block">{c.titleB}</span>
          </h2>
          <p className="mt-5 max-w-md text-[15px] leading-relaxed text-paper/60">{c.sub}</p>
          <div className="mt-8 flex items-center gap-3 text-sm text-paper/50">
            <span>{c.or}</span>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-acid/50 bg-acid/10 px-4 py-2 font-semibold text-acid transition hover:bg-acid hover:text-ink"
              data-cursor
            >
              <MessageCircle className="w-4 h-4" /> {c.wa}
            </a>
          </div>
        </div>

        <div className="rounded-3xl border border-paper/10 bg-ink2/50 p-7">
          {state === "done" ? (
            <div className="flex flex-col items-center gap-4 py-10 text-center">
              <span className="grid place-items-center w-14 h-14 rounded-full bg-acid text-ink">
                <Check className="w-7 h-7" />
              </span>
              <h3 className="font-display text-xl font-bold text-paper">{c.successTitle}</h3>
              <p className="max-w-xs text-sm text-paper/65">{c.successBody}</p>
              <button
                type="button"
                onClick={() => { setForm(EMPTY); setState("idle"); }}
                className="rounded-full border border-paper/20 px-5 py-2.5 text-sm text-paper/80 transition hover:border-acid hover:text-acid"
                data-cursor
              >
                {c.successAgain}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-3.5">
              <div className="grid gap-3.5 sm:grid-cols-2">
                <input required value={form.name} onChange={set("name")} placeholder={c.name} className={input} />
                <input required type="email" value={form.email} onChange={set("email")} placeholder={c.email} className={input} />
              </div>
              <div className="grid gap-3.5 sm:grid-cols-2">
                <input value={form.phone} onChange={set("phone")} placeholder={c.phone} className={input} />
                <input value={form.company} onChange={set("company")} placeholder={c.company} className={input} />
              </div>
              <select value={form.planName} onChange={set("planName")} className={input}>
                <option value="">{c.planNone}</option>
                {planNames.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <textarea required rows={4} value={form.message} onChange={set("message")} placeholder={c.message} className={input + " resize-none"} />
              {state === "error" && <p className="text-[13px] text-red-400">{c.error}</p>}
              <button
                type="submit"
                disabled={state === "sending"}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-acid px-6 py-3.5 text-sm font-bold text-ink transition hover:bg-paper disabled:opacity-60"
                data-cursor
              >
                {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {state === "sending" ? c.sending : c.submit}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </section>
  );
}
